import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import { Identifier, useGetList, useGetMany } from "react-admin";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { ResourceName, Task, User } from "@/types";
import { type ColorType, UIComponentSize } from "@/types/shared/uiComponents";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { CompactTable, type CompactTableColumn } from "@/components/common/CompactTable";
import { SectionHeader } from "../SectionHeader";
import { getProps } from "@/utils/helpers/columnsHelper";
import { getStatusColor } from "@/utils/helpers/getStatusColor";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { TaskDetailsDialog } from "@/resources/tasks/components/task-dialogs/TaskDetailsDialog";

// ==================== HELPERS ====================

/** Resolve column label from metadata, falling back to a provided default */
const getColumnLabel = (key: string, fallback?: string) => {
  const props = getProps(key, ResourceName.TASKS);
  return props.label.startsWith("Label missing") ? (fallback ?? key) : props.label;
};

const OBJECT_TYPE_LABELS: Record<string, string> = {
  clients: "Client",
  policies: "Policy",
  claimants: "Claimant",
  stoploss_claims: "Claim",
  "stoploss-claims": "Claim",
  reimbursements: "Reimbursement",
  documents: "Document",
};

const formatObjectType = (objectType: string): string => OBJECT_TYPE_LABELS[objectType] || objectType;

// ==================== TYPES ====================

interface ClaimsSummaryTasksProps {
  clientId: Identifier;
  onTasksLoad?: (tasks: Task[]) => void;
  isExpanded: boolean;
  onToggle: () => void;
}

// ==================== MAIN COMPONENT ====================

export const ClaimsSummaryTasks = ({ clientId, onTasksLoad, isExpanded, onToggle }: ClaimsSummaryTasksProps) => {
  const [open, setOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | undefined>();

  const { data: tasks = [], isLoading } = useGetList<Task>(
    `clients/${clientId}/tasks`,
    {
      filter: { display_claim_exhibit: true },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "due_date", order: "ASC" },
      meta: {
        "X-Fields":
          "id,title,description,status,due_date,assigned_user_id_ref,record,modified_datetime,display_claim_exhibit",
      },
    },
    { enabled: !!clientId }
  );

  const assignedUserIds = useMemo(
    () => [...new Set(tasks.map((t) => t.assigned_user_id_ref).filter((id): id is string => !!id))],
    [tasks]
  );

  const { data: assignedUsers = [] } = useGetMany<User>(
    ResourceName.USERS,
    {
      ids: assignedUserIds,
      meta: { "X-Fields": "id,full_name,preferred_name,email,user_principal_name" },
    },
    { enabled: assignedUserIds.length > 0 }
  );

  const userMap = useMemo(() => new Map(assignedUsers.map((user) => [user.id, user])), [assignedUsers]);

  // Only notify the parent once the query has settled. While loading, useGetList
  // returns a fresh `[]` default on every render, which would re-fire this effect
  // and trigger setState in the parent on a loop (→ "Maximum update depth exceeded",
  // blank tab until refresh). Once loaded, react-query returns a stable reference.
  //
  // NOTE: useClaimsSummaryData guards the same loop class with an idempotent state
  // reset, but this effect uses a load-gate instead — and the two should NOT be
  // harmonized. There we own the state (setSummaryRows), so keeping the empty ref
  // stable is what breaks the loop; here we only forward to a parent, so the only
  // lever is to not call during the unstable-`[]` loading window.
  useEffect(() => {
    if (isLoading) return;
    onTasksLoad?.(tasks);
  }, [tasks, onTasksLoad, isLoading]);

  const getAssignedTo = (task: Task): string => {
    if (!task.assigned_user_id_ref) return "Unassigned";
    const user = userMap.get(task.assigned_user_id_ref);
    if (!user) return "Loading...";
    return user.full_name || user.preferred_name || user.email || user.user_principal_name || "Unknown";
  };

  const getLinkedRecord = (task: Task): string => {
    if (!task.record?.object_type || !task.record?.object_id) return "—";
    const { object_type, label } = task.record;
    if (label) return `${formatObjectType(object_type)}: ${label}`;
    return `${formatObjectType(object_type)} #${task.record.object_id}`;
  };

  const columns: CompactTableColumn<Task>[] = useMemo(
    () => [
      {
        key: "title",
        label: getColumnLabel("title"),
        width: "1.5fr",
        render: (task) => {
          const titleEl = (
            <Typography
              variant="body2"
              fontWeight={500}
              onClick={() => {
                setSelectedTask(task);
                setOpen(true);
              }}
              sx={{
                cursor: "pointer",
                color: "primary.main",
                textDecoration: "underline",
                "&:hover": { color: "primary.dark" },
              }}
            >
              {task.title}
            </Typography>
          );
          return task.description ? (
            <Tooltip title={task.description} placement="bottom-start" arrow>
              {titleEl}
            </Tooltip>
          ) : (
            titleEl
          );
        },
      },
      {
        key: "status",
        label: getColumnLabel("status"),
        width: "100px",
        render: (task) => {
          const value = task.status;
          const label = value ? formatToTitleCase(value) : "Unknown";
          const color = getStatusColor(value) ?? ("default" as ColorType);
          return <StatusChip label={label} color={color} size={UIComponentSize.Small} />;
        },
      },
      {
        key: "due_date",
        label: getColumnLabel("due_date"),
        width: "100px",
        render: (task) => (
          <Typography variant="body2" color="text.secondary">
            {task.due_date ? format(new Date(task.due_date), "MMM d, yyyy") : "—"}
          </Typography>
        ),
      },
      {
        key: "assigned_to",
        label: getColumnLabel("assigned_user_id_ref"),
        width: "1fr",
        render: (task) => <Typography variant="body2">{getAssignedTo(task)}</Typography>,
      },
      {
        key: "record",
        label: getColumnLabel("record", "Linked Record"),
        width: "1fr",
        render: (task) => (
          <Typography variant="body2" color="text.secondary">
            {getLinkedRecord(task)}
          </Typography>
        ),
      },
    ],
    [userMap]
  );

  if (!clientId) return null;

  return (
    <SectionHeader
      title="Tasks"
      subtitle="Tasks flagged for the claims summary report"
      count={!isLoading ? tasks.length : undefined}
      collapsible
      isExpanded={isExpanded}
      onToggle={onToggle}
    >
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <CompactTable
          data={tasks}
          columns={columns}
          rowsPerPage={10}
          emptyMessage="No tasks flagged for this report."
        />
      )}

      <TaskDetailsDialog open={open} task={selectedTask} onClose={() => setOpen(false)} />
    </SectionHeader>
  );
};
